// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

type Props = {
  visible: boolean;
  pending: boolean;
  errorText?: string | null;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Last stop before an irreversible delete, so the destructive action is never the one a stray tap
 * lands on: cancel sits first and stays enabled even while the request is in flight.
 */
export default function DeleteAccountModal({ visible, pending, errorText, onConfirm, onClose }: Props) {
  const { t } = useI18n();

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose}>
        <Pressable style={styles.card} onPress={() => null} testID="delete-account-modal">
          <Text style={styles.title}>{t('profile.deleteAccountConfirmTitle')}</Text>
          <Text style={styles.body}>{t('profile.deleteAccountConfirmBody')}</Text>
          <Text style={styles.body}>{t('profile.deleteAccountConfirmHistory')}</Text>
          {errorText ? <Text style={styles.errorText}>{errorText}</Text> : null}
          <View style={styles.actions}>
            <Pressable
              style={styles.cancelButton}
              onPress={onClose}
              testID="delete-account-cancel"
              accessibilityLabel={t('profile.deleteAccountCancel')}
            >
              <Text style={styles.cancelButtonText}>{t('profile.deleteAccountCancel')}</Text>
            </Pressable>
            <Pressable
              style={[styles.confirmButton, pending && styles.confirmButtonDisabled]}
              onPress={onConfirm}
              disabled={pending}
              testID="delete-account-confirm"
              accessibilityLabel={t('profile.deleteAccountConfirm')}
            >
              <Text style={styles.confirmButtonText}>
                {pending ? t('profile.deleteAccountPending') : t('profile.deleteAccountConfirm')}
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.5),
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 380,
    backgroundColor: appColors.background.panel,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.status.dangerBg,
    padding: 16,
    gap: 12,
  },
  title: {
    color: appColors.text.primary,
    fontSize: 18,
    fontWeight: '700',
  },
  body: {
    color: appColors.text.secondary,
    fontSize: 14,
    lineHeight: 20,
  },
  errorText: {
    color: appColors.status.errorTextMuted,
    fontSize: 13,
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 4,
  },
  cancelButton: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  cancelButtonText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  confirmButton: {
    borderRadius: 12,
    backgroundColor: appColors.status.dangerBg,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  confirmButtonDisabled: {
    opacity: 0.6,
  },
  confirmButtonText: {
    color: appColors.status.dangerTextSoft,
    fontSize: 14,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
});
